import React, { useState, useEffect } from 'react';
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  Paper, CircularProgress, IconButton, Collapse, Box
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { catalogApi } from '../../../api/catalogApi.js';
import { AppLayout } from '../../app/appLayout.js';
import { Modal } from '../../shared';

const ViewRecipesScreen = () => {
  const [items, setItems] = useState([]);
  // Store the key of the currently expanded row (or null)
  const [expandedRow, setExpandedRow] = useState(null);
  // Details are cached by rowKey so we don't refetch repeatedly
  const [details, setDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  // Add-to-recipe modal state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [activeRecipeName, setActiveRecipeName] = useState('');
  const [allItems, setAllItems] = useState([]); // dropdown options
  const [selectedItemId, setSelectedItemId] = useState('');
  const [numServings, setNumServings] = useState('1');
  const [loadingItems, setLoadingItems] = useState(false);
  const [submittingAdd, setSubmittingAdd] = useState(false);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const { data } = await catalogApi.getRecipes();
        setItems(data.rows);
      } catch (error) {
        console.error('Error fetching recipes:', error);
      }
    };
    fetchItems();
  }, []);

  const getRowKey = (item) => {
    // Use id if present; otherwise fall back to a stable unique field like name
    // If both could collide in your data, concatenate both.
    return item?.id ?? item?.name;
  };

  const handleRowClick = async (item) => {
    const rowKey = getRowKey(item);

    setExpandedRow((prev) => (prev === rowKey ? null : rowKey));

    // Only fetch if we don't already have details for this row
    if (!details[rowKey]) {
      setLoadingDetails((prev) => ({ ...prev, [rowKey]: true }));
      try {
        const { data } = await catalogApi.getRecipeDetails(item.name);
        setDetails((prev) => ({ ...prev, [rowKey]: data.row.items }));
      } catch (error) {
        console.error('Error fetching recipe details:', error);
      } finally {
        setLoadingDetails((prev) => ({ ...prev, [rowKey]: false }));
      }
    }
  };

  const refetchRecipeDetails = async (recipeName) => {
    const dummy = { name: recipeName };
    const rowKey = getRowKey(dummy);
    setLoadingDetails((prev) => ({ ...prev, [rowKey]: true }));
    try {
      const { data } = await catalogApi.getRecipeDetails(recipeName);
      setDetails((prev) => ({ ...prev, [rowKey]: data.row.items }));
    } catch (error) {
      console.error('Error refetching recipe details:', error);
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [rowKey]: false }));
    }
  };

  const openAddModal = async (recipeName) => {
    setActiveRecipeName(recipeName);
    setIsAddOpen(true);
    setSelectedItemId('');
    setNumServings('1');
    setLoadingItems(true);
    try {
      const resp = await catalogApi.getItems();
      const root = resp?.data?.rows ?? resp?.data ?? resp ?? [];
      const list = Array.isArray(root)
        ? root
        : Array.isArray(root.items)
        ? root.items
        : Array.isArray(root.rows)
        ? root.rows
        : [];
      const mapped = list.map((it) => ({
        id: it?.id ?? it?.itemID ?? it?.itemId ?? it?.itemid ?? it?.name,
        label: [it?.brand, it?.name].filter(Boolean).join(' - ') || it?.name || String(it),
      })).filter((m) => !!m.id);
      setAllItems(mapped);
      if (mapped.length > 0) setSelectedItemId(String(mapped[0].id));
    } catch (e) {
      console.error('Error loading items for add modal', e);
      setAllItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const closeAddModal = () => {
    setIsAddOpen(false);
  };

  const handleAddSubmit = async () => {
    const servingsNum = Number(numServings);
    if (!activeRecipeName || !selectedItemId || Number.isNaN(servingsNum) || servingsNum <= 0) return;
    setSubmittingAdd(true);
    try {
      const resp = await catalogApi.addToRecipe(activeRecipeName, selectedItemId, servingsNum);
      if (resp?.status === true || resp?.ok === true) {
        await refetchRecipeDetails(activeRecipeName);
        closeAddModal();
      } else {
        // still refetch for eventual consistency
        await refetchRecipeDetails(activeRecipeName);
        closeAddModal();
      }
    } catch (e) {
      console.error('addToRecipe error', e);
      await refetchRecipeDetails(activeRecipeName);
      closeAddModal();
    } finally {
      setSubmittingAdd(false);
    }
  };

  const handleRemove = async (recipeName, detail) => {
    const itemId = detail?.itemID ?? detail?.itemId ?? detail?.id;
    if (!itemId) {
      console.error('No item id found on detail for removal');
      return;
    }
    const confirm = window.confirm('Remove this item from the recipe?');
    if (!confirm) return;
    try {
      await catalogApi.removeFromRecipe(recipeName, itemId);
    } catch (e) {
      console.error('removeFromRecipe error', e);
    } finally {
      await refetchRecipeDetails(recipeName);
    }
  };

  const calculateSummary = (items) => {
    let totals = items.reduce(
      (acc, item) => ({
        calories: acc.calories + (item.calories ?? 0),
        totalFatG: acc.totalFatG + (item.totalFatG ?? 0),
        saturatedFatG: acc.saturatedFatG + (item.saturatedFatG ?? 0),
        choleterolMg: acc.choleterolMg + (item.choleterolMg ?? 0),
        sodiumMg: acc.sodiumMg + (item.sodiumMg ?? 0),
        totalCarbG: acc.totalCarbG + (item.totalCarbG ?? 0),
        totalSugarG: acc.totalSugarG + (item.totalSugarG ?? 0),
        proteinG: acc.proteinG + (item.proteinG ?? 0),
        fiberG: acc.fiberG + (item.fiberG ?? 0),
      }),
      {
        calories: 0,
        totalFatG: 0,
        saturatedFatG: 0,
        choleterolMg: 0,
        sodiumMg: 0,
        totalCarbG: 0,
        totalSugarG: 0,
        proteinG: 0,
        fiberG: 0,
      }
    );

    // Round each property to 2 decimals
    return Object.fromEntries(
      Object.entries(totals).map(([key, value]) => [key, Number(value.toFixed(2))])
    );
  };

  return (
    <AppLayout>
      <Paper style={{ padding: 20, margin: 20, overflowX: 'auto' }}>
        <h2>Recipes</h2>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell><b>Name</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length > 0 ? (
              items.map((item) => {
                const rowKey = getRowKey(item);
                const recipeDetails = details[rowKey] ?? [];
                const summary = recipeDetails.length > 0 ? calculateSummary(recipeDetails) : null;

                return (
                  <React.Fragment key={rowKey}>
                    <TableRow onClick={() => handleRowClick(item)} style={{ cursor: 'pointer' }}>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={expandedRow === rowKey ? 'Collapse row' : 'Expand row'}
                        >
                          {expandedRow === rowKey ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                        <span>{item.name}</span>
                        <IconButton
                          size="small"
                          aria-label="Add item to recipe"
                          onClick={(e) => { e.stopPropagation(); openAddModal(item.name); }}
                        >
                          <AddIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
                        <Collapse in={expandedRow === rowKey} timeout="auto" unmountOnExit>
                          <Box margin={2}>
                            {loadingDetails[rowKey] ? (
                              <CircularProgress size={24} />
                            ) : recipeDetails.length > 0 ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell><b>Brand</b></TableCell>
                                    <TableCell><b>Item Name</b></TableCell>
                                    <TableCell><b>Serving Size</b></TableCell>
                                    <TableCell><b>Calories</b></TableCell>
                                    <TableCell><b>Total Fat (g)</b></TableCell>
                                    <TableCell><b>Saturated Fat (g)</b></TableCell>
                                    <TableCell><b>Cholesterol (mg)</b></TableCell>
                                    <TableCell><b>Sodium (mg)</b></TableCell>
                                    <TableCell><b>Total Carbs (g)</b></TableCell>
                                    <TableCell><b>Sugars (g)</b></TableCell>
                                    <TableCell><b>Protein (g)</b></TableCell>
                                    <TableCell><b>Fiber (g)</b></TableCell>
                                    <TableCell />
                                  </TableRow>
                                  {summary && (
                                    <TableRow style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                                      <TableCell colSpan={3}><b>Summary (Total)</b></TableCell>
                                      <TableCell>{summary.calories}</TableCell>
                                      <TableCell>{summary.totalFatG}</TableCell>
                                      <TableCell>{summary.saturatedFatG}</TableCell>
                                      <TableCell>{summary.choleterolMg}</TableCell>
                                      <TableCell>{summary.sodiumMg}</TableCell>
                                      <TableCell>{summary.totalCarbG}</TableCell>
                                      <TableCell>{summary.totalSugarG}</TableCell>
                                      <TableCell>{summary.proteinG}</TableCell>
                                      <TableCell>{summary.fiberG}</TableCell>
                                      <TableCell />
                                    </TableRow>
                                  )}
                                </TableHead>
                                <TableBody>
                                  {recipeDetails.map((detail) => (
                                    <TableRow key={detail.id ?? `${detail.name}-${detail.brand}`}>
                                      <TableCell>{detail.brand ?? 'N/A'}</TableCell>
                                      <TableCell>{detail.name ?? 'N/A'}</TableCell>
                                      <TableCell>{`${detail.servingSize ?? 'N/A'} ${detail.servingUnit ?? ''}`}</TableCell>
                                      <TableCell>{detail.calories ?? 0}</TableCell>
                                      <TableCell>{detail.totalFatG ?? 0}</TableCell>
                                      <TableCell>{detail.saturatedFatG ?? 0}</TableCell>
                                      <TableCell>{detail.choleterolMg ?? 0}</TableCell>
                                      <TableCell>{detail.sodiumMg ?? 0}</TableCell>
                                      <TableCell>{detail.totalCarbG ?? 0}</TableCell>
                                      <TableCell>{detail.totalSugarG ?? 0}</TableCell>
                                      <TableCell>{detail.proteinG ?? 0}</TableCell>
                                      <TableCell>{detail.fiberG ?? 0}</TableCell>
                                      <TableCell>
                                        <IconButton
                                          size="small"
                                          aria-label="Remove from recipe"
                                          onClick={(e) => { e.stopPropagation(); handleRemove(item.name, detail); }}
                                        >
                                          <CloseIcon />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <p>No details found.</p>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center">No recipes found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {isAddOpen && (
        <Modal title={`Add Item to ${activeRecipeName}`} onClose={closeAddModal}>
          <div style={{ display: 'grid', gap: 12, minWidth: 320 }}>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 600 }}>Item</span>
              {loadingItems ? (
                <div style={{ opacity: 0.7 }}>Loading items…</div>
              ) : (
                <select
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd' }}
                >
                  {allItems.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              )}
              {(!loadingItems && allItems.length === 0) && (
                <small style={{ opacity: 0.7 }}>No items available.</small>
              )}
            </label>

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 600 }}>Num Servings</span>
              <input
                type="number"
                inputMode="decimal"
                step="any"
                min="0.0000001"
                value={numServings}
                onChange={(e) => setNumServings(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd' }}
              />
              <small style={{ opacity: 0.7 }}>Greater than 0. Decimals allowed.</small>
            </label>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                onClick={closeAddModal}
                style={{ background: 'transparent', border: '1px solid #ccc', padding: '8px 12px', borderRadius: 8, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                disabled={submittingAdd || !activeRecipeName || !selectedItemId || Number(numServings) <= 0}
                onClick={handleAddSubmit}
                style={{ background: '#1E88E5', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: submittingAdd ? 0.7 : 1 }}
              >
                {submittingAdd ? 'Submitting…' : 'Add'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
};

export { ViewRecipesScreen };
